/**
 * Bearer-token handling — the one place in the app that knows the token exists.
 *
 * The server requires `Authorization: Bearer <token>` on every /api/* call and
 * there is no login flow: the token is procured out-of-band and pasted in once.
 * We keep it in localStorage; a 401 means it is wrong or was rotated, so it is
 * discarded and the user is asked again.
 *
 * The prompt UI is *not* implemented here. A prompter is registered at startup
 * (see ui/tokenPrompt.ts), which keeps this module free of DOM concerns and
 * makes it trivially testable.
 */

const TOKEN_STORAGE_KEY = "vineelernote.apiToken";

export type TokenPromptReason = "missing" | "rejected";

export type TokenPrompter = (reason: TokenPromptReason) => Promise<string | null>;

/** Thrown when no token is available and the user declined to supply one. */
export class MissingTokenError extends Error {
  constructor(message = "No API token available.") {
    super(message);
    this.name = "MissingTokenError";
  }
}

let prompter: TokenPrompter | null = null;
let pendingPrompt: Promise<string> | null = null;

export function registerTokenPrompter(next: TokenPrompter): void {
  prompter = next;
}

export function getStoredToken(): string | null {
  try {
    const value = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    return value === null || value === "" ? null : value;
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* storage unavailable; the token then lives only for this page load */
  }
}

export function clearStoredToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

export function hasStoredToken(): boolean {
  return getStoredToken() !== null;
}

async function promptForToken(reason: TokenPromptReason): Promise<string> {
  if (prompter === null) {
    throw new MissingTokenError("No token prompter registered.");
  }

  // Collapse concurrent requests so a page issuing three parallel calls with an
  // empty store shows one dialog, not three.
  const existing = pendingPrompt;
  if (existing !== null) {
    return existing;
  }

  const active = prompter;
  const started = (async (): Promise<string> => {
    const entered = await active(reason);
    if (entered === null || entered.trim() === "") {
      throw new MissingTokenError();
    }
    const token = entered.trim();
    storeToken(token);
    return token;
  })();

  pendingPrompt = started;
  // Whatever the outcome, the next caller starts a fresh prompt.
  void started
    .catch(() => undefined)
    .finally(() => {
      pendingPrompt = null;
    });

  return started;
}

/** The token to send, prompting for one if the store is empty. */
export async function requireToken(): Promise<string> {
  const stored = getStoredToken();
  if (stored !== null) {
    return stored;
  }
  return promptForToken("missing");
}

/**
 * Called after a 401: the stored token is bad, so drop it and ask again.
 * Returns the replacement token.
 */
export async function refreshTokenAfterRejection(): Promise<string> {
  clearStoredToken();
  return promptForToken("rejected");
}
